import { IArticleCategory } from '@/src/interfaces/generated/contentful';
import { Box } from '@/src/packages/components/ui/Box';
import { Heading } from '@/src/packages/components/ui/Heading';
import { Link } from '@/src/packages/components/ui/Link';
import { paths } from '@/src/packages/routes';
import { pathParams } from '@/src/packages/routes/pathParams.enum';
import { getRoute } from '@/src/packages/utils/routes';
import { format, parseISO } from 'date-fns';
import Image from 'next/image';
import NextLink from 'next/link';
import { VariantProps, tv } from 'tailwind-variants';

const articleCardImageStyles = tv({
  base: 'relative block',
  variants: {
    ratio: {
      '4/3': 'aspect-[4/3]',
      '3/4': 'aspect-[3/4]',
    },
  },
});

type IArticleCardProps = {
  title: string;
  href: string;
  summary?: string;
  publishedAt?: string;
  categories?: IArticleCategory[];
  coverImageUrl: string;
  imageAspectRatio?: VariantProps<typeof articleCardImageStyles>['ratio'];
};

const ArticleCardImage = (props: IArticleCardProps) => {
  const { coverImageUrl, title, href, imageAspectRatio } = props;

  return (
    <NextLink
      href={href}
      className={articleCardImageStyles({ ratio: imageAspectRatio })}
    >
      <Image
        src={coverImageUrl}
        fill
        alt={title || ''}
        className="object-cover"
        sizes="33vw"
      />
    </NextLink>
  );
};

const ArticleCardCategories = (props: IArticleCardProps) => {
  const { categories } = props;

  if (!categories) return <></>;

  return (
    <>
      {categories.map((category, i) => {
        // Temp until work out what'\s going on with Contentful
        if (category.fields) {
          return (
            <Link asChild variant="solid" key={i}>
              <NextLink
                href={getRoute({
                  route: paths.BLOG_CATEGORY_INDEX,
                  params: {
                    [pathParams.SLUG]: category.fields.slug,
                  },
                })}
              >
                {category.fields.name}
              </NextLink>
            </Link>
          );
        }
      })}
    </>
  );
};

const ArticleCardHeader = (props: IArticleCardProps) => {
  const { title, publishedAt, href } = props;

  return (
    <Box className="space-y-6">
      <ArticleCardCategories {...props} />
      <Box className="space-y-2">
        <Heading as="h2" className="line-clamp-2 text-2xl leading-tight">
          <NextLink href={href}>{title}</NextLink>
        </Heading>

        {publishedAt && (
          <Box className="text-text-secondary">
            {format(parseISO(publishedAt), 'MMM dd, yyyy')}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const ArticleCard = ({
  imageAspectRatio = '3/4',
  ...rest
}: IArticleCardProps) => {
  return (
    <Box as="article" className="space-y-3">
      <ArticleCardImage imageAspectRatio={imageAspectRatio} {...rest} />
      <ArticleCardHeader {...rest} />
    </Box>
  );
};
